import { Directive, ElementRef, HostListener, Input } from '@angular/core';
import { Subscription } from 'rxjs';
import { MenuComponent } from './menu.component';
import { Keys } from '../../../_core/utils/keys';
import { PopoverTriggerDirective } from '../popover/popover-trigger.directive';

@Directive({
  selector: '[opac-menu-trigger]'
})
export class MenuTriggerDirective extends PopoverTriggerDirective {
  // State variables
  private _isSubmenu = false;
  private _menu: MenuComponent;
  // Handler binding needed so you can use `this` referred as this class,
  // and they can be removed with `removeEventListener`
  private handlerMenuKeydown = this.handleMenuKeydown.bind(this);
  private handlerDocumentKeydown = this.handleDocumentKeydown.bind(this);
  private selectedSubscription?: Subscription;

  get isSubmenu(): boolean {
    return this._isSubmenu;
  }
  set isSubmenu(value: boolean) {
    this._isSubmenu = value;
    this._menu.isSubmenu = value;
  }

  /**
   * Listens for trigger element click
   */
  @HostListener('click', ['$event'])
  onHostClick(event: Event) {
    event.preventDefault();
    super.onHostClick(event);
    this.elementRef.nativeElement.focus?.();
  }

  /**
   * Listens for trigger element keydown
   */
  @HostListener('keydown', ['$event'])
  onHostKeydown(event: KeyboardEvent) {
    super.onHostKeydown(event);

    // Let `menu-item.component.ts` handle open interactions when menu is submenu
    if (this.isSubmenu) {
      return;
    }

    const menuItems = this.menu.items;
    const firstMenuItem = this.menu.items.first;
    const lastMenuItem = this.menu.items.last;

    if ([Keys.ArrowUp, Keys.ArrowDown, Keys.Home, Keys.End].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();

      if (!this.isOpen) {
        this.showPopover()
      }

      if (menuItems.length === 0) {
        return;
      }

      if ([Keys.ArrowDown, Keys.End].includes(event.key)) {
        this.menu.setCurrentItem(firstMenuItem);

        requestAnimationFrame(() => {
          firstMenuItem.focus();
        });
      }

      if ([Keys.ArrowUp, Keys.Home].includes(event.key)) {
        this.menu.setCurrentItem(lastMenuItem);

        requestAnimationFrame(() => {
          lastMenuItem.focus();
        });
      }
    }
  }

  /**
   * Give directive the menu component's instance.
   */
  @Input('opac-menu-trigger') set menu(value: MenuComponent) {
    // Set parent directive the popover element to handle it.
    this.popoverComponent = value.popover;
    this._menu = value;
  }
  get menu() {
    return this._menu;
  }

  constructor(readonly elementRef: ElementRef<HTMLElement>) {
    super(elementRef);

    this.minWidth = 160;
    this.maxWidth = 340;
    this.maxHeight = 530;
  }

  /**
   * @internal
   * Closes menu and unsubscribe for event listeners
   */
  closePopover() {
    super.closePopover();

    this.selectedSubscription?.unsubscribe();
    this.menu.element.nativeElement.removeEventListener('keydown', this.handlerMenuKeydown);
    document.removeEventListener('keydown', this.handlerDocumentKeydown);
  }

  /**
   * @internal
   * Opens menu and subscribe for event listeners
   */
  async showPopover() {
    await super.showPopover();

    this.selectedSubscription = this.menu.selected.subscribe(() => this.closePopover());
    this.menu.element.nativeElement.addEventListener('keydown', this.handlerMenuKeydown);
    document.addEventListener('keydown', this.handlerDocumentKeydown);
  }

  private handleMenuKeydown(event: KeyboardEvent) {
    if (this.isOpen && event.key === Keys.Escape) {
      event.stopPropagation();
      this.closePopover();
      this.elementRef.nativeElement.focus?.();
    }
  }

  private handleDocumentKeydown(event: KeyboardEvent) {
    if (this.isOpen && event.key === Keys.Escape) {
      event.stopPropagation();
      this.closePopover();
      this.elementRef.nativeElement.focus?.();
    }

    if (this.isOpen && event.key === Keys.Tab) {
      event.preventDefault();
      this.elementRef.nativeElement.focus?.();
      this.closePopover();
    }
  }
}
